import { ChordLib, getDefaultProgressionForScale } from '@tunasong/music-lib'
import type { Chord } from '@tunasong/music-lib'
import type { NoteName, Scale, ScaleType } from '@tunasong/schemas'

/**
 * Cadence Lab + Melody Writer v1 supports these eight scale types — the
 * subset for which the default-progression table is Bowie-locked. Other
 * `ScaleType` values (e.g. `harmonicminor`, `majorpentatonic`) fall back
 * to the default scale (C major).
 */
const SUPPORTED_SCALE_TYPES: readonly ScaleType[] = [
  'major',
  'minor',
  'dorian',
  'mixolydian',
  'phrygian',
  'lydian',
  'aeolian',
  'locrian',
]

const isSupportedScaleType = (value: string): value is ScaleType =>
  (SUPPORTED_SCALE_TYPES as readonly string[]).includes(value)

/**
 * The minimum chord count for Cadence Lab. Mirrors the `MIN_CHORDS = 3`
 * guard in `<CadenceLab>`. If a URL-supplied chord list has fewer than this,
 * the whole param falls back to the per-scale default.
 */
const MIN_CHORDS = 3

export const DEFAULT_CADENCE_LAB_SCALE: Scale = { root: 'C', type: 'major' }

/**
 * Parse the `?scale=<root>-<type>` URL param. Format is `{root}-{type}` where
 * `type` is one of the v1-supported scale types. Silently falls back to
 * C major on any unknown / malformed value (matches the other standalone
 * tools' parsers).
 *
 * Spec: docs/superpowers/specs/2026-05-20-drawer-tools-standalone-routes-design.md §3.2
 */
export const parseCadenceLabScaleParam = (raw: string | null): Scale => {
  if (raw === null || raw === '') {
    return DEFAULT_CADENCE_LAB_SCALE
  }
  const parts = raw.split('-')
  if (parts.length !== 2) {
    return DEFAULT_CADENCE_LAB_SCALE
  }
  const [rootRaw, typeRaw] = parts
  if (!isValidNoteName(rootRaw)) {
    return DEFAULT_CADENCE_LAB_SCALE
  }
  if (!isSupportedScaleType(typeRaw)) {
    return DEFAULT_CADENCE_LAB_SCALE
  }
  return { root: rootRaw, type: typeRaw }
}

/**
 * Parse the `?chords=<comma-list>` URL param. Each token is parsed via
 * `ChordLib.fromName`; if ANY token fails OR the resulting list has fewer
 * than `MIN_CHORDS` entries, the whole param falls back to the per-scale
 * default progression (strict mode — no partial parsing).
 *
 * Spec: docs/superpowers/specs/2026-05-20-drawer-tools-standalone-routes-design.md §3.2
 */
export const parseCadenceLabChordsParam = (raw: string | null, scale: Scale): Chord[] => {
  if (raw === null || raw === '') {
    return getDefaultProgressionForScale(scale)
  }
  const tokens = raw.split(',').map(t => t.trim()).filter(t => t.length > 0)
  if (tokens.length < MIN_CHORDS) {
    return getDefaultProgressionForScale(scale)
  }
  const parsed: Chord[] = []
  for (const token of tokens) {
    const chord = ChordLib.fromName(token)
    if (!chord) {
      return getDefaultProgressionForScale(scale)
    }
    parsed.push(chord)
  }
  return parsed
}

// Valid note-name set — the same enum the schema accepts. Inline here to
// avoid pulling the full Zod schema into a hot path (parsing runs on every
// route mount).
const VALID_NOTE_NAMES = new Set<string>([
  'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B',
  'Dbb', 'Db', 'Ebb', 'Eb', 'Fb', 'E#', 'Gb', 'Abb', 'Ab', 'Bbb', 'Bb', 'Cb', 'B#',
])

const isValidNoteName = (value: string): value is NoteName => VALID_NOTE_NAMES.has(value)
